/**
 * payment-methods.ts — Nhãn phương thức thanh toán, đọc tham số quay về từ cổng thanh
 * toán EvoPay (hàm thuần có test).
 */
import type { BillingOrder, CheckoutForm, CheckoutResult, PaymentMethod } from '@/hooks/use-billing'
import { i18n } from '@/lib/i18n'

/** Một dòng tiêu đề cho từng loại thanh toán: `title` (đậm) + `hint` (liệt kê các phương thức trả được). Không dùng nhãn dài của server. */
export const METHOD_COPY: Record<PaymentMethod, { title: string; hint: string }> = {
  get card() { return { title: `${i18n.t('billing:methodCopy.card.title')}:`, hint: i18n.t('billing:methodCopy.card.hint') } },
  get bank() { return { title: `${i18n.t('billing:methodCopy.bank.title')}:`, hint: i18n.t('billing:methodCopy.bank.hint') } },
  get paypal() { return { title: `${i18n.t('billing:methodCopy.paypal.title')}:`, hint: i18n.t('billing:methodCopy.paypal.hint') } },
  get sepay() { return { title: `${i18n.t('billing:methodCopy.sepay.title')}:`, hint: i18n.t('billing:methodCopy.sepay.hint') } },
  get tingee() { return { title: `${i18n.t('billing:methodCopy.tingee.title')}:`, hint: i18n.t('billing:methodCopy.tingee.hint') } },
}

/** Tên ngắn của phương thức; id lạ → dùng nhãn server gửi. */
export function methodTitle(id: string, fallback = ''): string {
  const t = METHOD_COPY[id as PaymentMethod]?.title
  return t ? t.replace(/:$/, '') : (fallback || id)
}

/** Phương thức mặc định khi khách chưa chọn: Chuyển khoản QR nếu đang bật (phổ biến nhất với khách Việt), không thì phương thức đầu. */
export const DEFAULT_METHOD: PaymentMethod = 'bank'

/** Nhãn ngắn cho bảng lịch sử. Phương thức lạ → hiện nguyên giá trị. */
export function methodShortLabel(provider: string): string {
  if (provider === 'evopay_card') return i18n.t('billing:methodShortLabel.evopayCard')
  if (provider === 'evopay_bank') return i18n.t('billing:methodShortLabel.evopayBank')
  if (provider === 'evopay_paypal') return 'PayPal'
  if (provider === 'evopay_sepay') return i18n.t('billing:methodShortLabel.evopaySepay')
  if (provider === 'evopay_tingee') return i18n.t('billing:methodShortLabel.evopayTingee')
  if (provider === 'sepay_pg') return 'SePay'
  // Đơn chuyển khoản ngân hàng kiểu cũ (đã bỏ) vẫn còn trong lịch sử.
  if (provider === 'sepay') return i18n.t('billing:methodShortLabel.bankTransfer')
  if (provider === 'zalopay') return 'ZaloPay'
  // Đơn tạo từ báo giá thủ công (Platform xác nhận khách đã chuyển khoản).
  if (provider === 'bank_transfer') return i18n.t('billing:methodShortLabel.bankTransfer')
  return provider
}

export type PaymentReturnResult = 'error' | 'cancel' | 'return' | 'bank'

/**
 * Đọc URL quay về (trang kết quả chung /billing/result; các trang /billing, /billing/upgrade, /billing/addon vẫn đọc được cho đơn cũ):
 *  - EvoPay: payment=return&order=<id đơn>&evopay_status=succeeded|pending|failed|cancelled|expired
 *  - Chuyển khoản EvoPay (trang tự mở sau checkout): payment=bank&order=<id đơn>
 * Kết quả trên URL chỉ để hiển thị lời nhắn ban đầu — trạng thái thật luôn hỏi server.
 */
export function parsePaymentReturn(params: URLSearchParams): { result: PaymentReturnResult; orderRef: string | null } | null {
  const p = params.get('payment')
  if (p !== 'return' && p !== 'bank') return null
  const ref = params.get('order')?.trim() || null
  // EvoPay gắn trạng thái lúc quay về: huỷ / lỗi → không chờ tiếp (server vẫn đối soát).
  const ev = params.get('evopay_status')
  // PayPal: khách bấm "Huỷ" thì trạng thái bên PayPal vẫn là "đã tạo" (pending) — chỉ có gợi ý evopay_result=cancel|error (không dùng được nếu đã succeeded).
  const hint = ev === 'succeeded' ? null : params.get('evopay_result')
  const result: PaymentReturnResult = p === 'return' && (ev === 'cancelled' || hint === 'cancel') ? 'cancel'
    : p === 'return' && (ev === 'failed' || ev === 'expired' || hint === 'error') ? 'error'
      : p
  return { result, orderRef: ref && ref.length <= 64 ? ref : null }
}

/** Trang thanh toán 2C2P (EvoPay trả) — chỉ cho chuyển sang tên miền 2C2P qua https. */
export function isTwoC2PCheckoutUrl(url: string): boolean {
  try {
    const u = new URL(url)
    return u.protocol === 'https:' && (u.hostname === '2c2p.com' || u.hostname.endsWith('.2c2p.com'))
  } catch {
    return false
  }
}

/** Trang duyệt thanh toán PayPal — chỉ https *.paypal.com. */
export function isPayPalCheckoutUrl(url: string): boolean {
  try {
    const u = new URL(url)
    return u.protocol === 'https:' && (u.hostname === 'paypal.com' || u.hostname.endsWith('.paypal.com'))
  } catch {
    return false
  }
}

/** VND → USD (cent) như server: làm tròn lên. Chỉ để hiển thị — số thật do server chốt lúc đặt hàng. */
export function usdCentsOf(vnd: number, vndPerUsd: number): number {
  return Math.ceil((vnd * 100) / vndPerUsd)
}

export function formatUsd(cents: number): string {
  return `${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`
}

// ── Trang kết quả chung: /billing/result ─────────────────────────────

export const RESULT_ROUTE = '/billing/result'

/**
 * Đường nội bộ hợp lệ để nút "Tiếp tục" đưa khách về (trang đang làm dở). Chặn open-redirect: chỉ một dấu `/` đầu,
 * không `//`, `\\`, ký tự điều khiển; không quay vòng về chính trang kết quả. Khớp `safeInternalPath` ở server.
 */
export function safeFrom(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 300) return null
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\') || /[\u0000-\u001f\u007f]/.test(raw)) return null
  if (raw === RESULT_ROUTE || raw.startsWith(`${RESULT_ROUTE}?`) || raw.startsWith(`${RESULT_ROUTE}/`)) return null
  return raw
}

/** `/billing/result` (+ `payment`, `order`, `from` nếu có) — gửi server làm `returnTo` hoặc dùng để chuyển trang. */
export function resultPath(from?: string | null, order?: { payment: 'bank' | 'return'; order: string }): string {
  const qs = new URLSearchParams()
  if (order) { qs.set('payment', order.payment); qs.set('order', order.order) }
  const f = safeFrom(from)
  if (f) qs.set('from', f)
  const s = qs.toString()
  return s ? `${RESULT_ROUTE}?${s}` : RESULT_ROUTE
}

/** Trang hiện tại (đường + query) để quay lại sau khi thanh toán; bỏ tham số kết quả thanh toán cũ. null nếu đang ở trang kết quả. */
export function currentPageForReturn(loc: Pick<Location, 'pathname' | 'search'> = window.location): string | null {
  const params = new URLSearchParams(loc.search)
  for (const k of ['payment', 'order', 'apptransid', 'evopay_payment_id', 'evopay_order_ref', 'evopay_status', 'evopay_result']) params.delete(k)
  const qs = params.toString()
  return safeFrom(`${loc.pathname}${qs ? `?${qs}` : ''}`)
}

/** Trang thanh toán của Cổng Tingee (production payment-gateway.tingee.vn / sandbox uat-payment-gateway.tingee.vn). */
export function isTingeeCheckoutUrl(url: string): boolean {
  try {
    const u = new URL(url)
    return u.protocol === 'https:' && (u.hostname === 'tingee.vn' || u.hostname.endsWith('.tingee.vn'))
  } catch {
    return false
  }
}

/** Trang thanh toán của Cổng SePay (production / sandbox) — chỉ POST form sang đúng tên miền SePay. */
export function isSePayCheckoutUrl(url: string): boolean {
  try {
    const u = new URL(url)
    return u.protocol === 'https:' && (u.hostname === 'pay.sepay.vn' || u.hostname === 'pay-sandbox.sepay.vn')
  } catch {
    return false
  }
}

type FormDoc = Pick<Document, 'createElement' | 'body'>

/** Dựng <form method="POST"> ẩn với các trường ĐÚNG thứ tự rồi submit sang trang Cổng SePay. */
export function submitCheckoutForm(form: CheckoutForm, doc: FormDoc = document): void {
  if (!isSePayCheckoutUrl(form.action)) throw new Error(i18n.t('billing:errors.invalidCheckoutUrl'))
  const el = doc.createElement('form')
  el.method = 'POST'
  el.action = form.action
  el.style.display = 'none'
  for (const [name, value] of form.fields) {
    const input = doc.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    el.appendChild(input)
  }
  doc.body.appendChild(el)
  el.submit()
}

/** Đưa khách đi thanh toán theo kết quả checkout (trừ đơn 0đ `credit` — xử lý trước). */
export function goToPayment(r: Exclude<CheckoutResult, { method: 'credit' }>, loc: Pick<Location, 'assign'> = window.location, doc?: FormDoc): void {
  if ('form' in r) return submitCheckoutForm(r.form, doc)
  if ('redirectUrl' in r) {
    const ok = r.method === 'paypal' ? isPayPalCheckoutUrl(r.redirectUrl)
      : r.method === 'tingee' ? isTingeeCheckoutUrl(r.redirectUrl)
      : isTwoC2PCheckoutUrl(r.redirectUrl)
    if (!ok) throw new Error(i18n.t('billing:errors.invalidCheckoutUrl'))
    return loc.assign(r.redirectUrl)
  }
  // Chuyển khoản: khung QR hiện ở trang kết quả /billing/result?payment=bank&order=<id> (từ `from` đã lưu cùng đơn).
  loc.assign(resultPath(readReturnFrom(), { payment: 'bank', order: r.orderId }))
}

/** Đơn đang chờ thanh toán — lưu trước khi chuyển sang cổng thanh toán, để theo dõi tiếp khi quay về. */
const PENDING_ORDER_KEY = 'billing:pendingOrderId'
/** Trang khách đang làm dở lúc bấm thanh toán — nút "Tiếp tục" ở trang kết quả đưa về đây (sống qua lần chuyển sang cổng thanh toán). */
const RETURN_FROM_KEY = 'billing:returnFrom'

export function readPendingOrder(): string | null {
  try { return sessionStorage.getItem(PENDING_ORDER_KEY) } catch { return null }
}
export function readReturnFrom(): string | null {
  try { return safeFrom(sessionStorage.getItem(RETURN_FROM_KEY)) } catch { return null }
}
/** `id` = null → xoá cả đơn lẫn trang quay về. `from`: undefined = giữ nguyên, null = xoá, chuỗi = lưu (nếu hợp lệ). */
export function writePendingOrder(id: string | null, from?: string | null) {
  try {
    if (id) sessionStorage.setItem(PENDING_ORDER_KEY, id)
    else { sessionStorage.removeItem(PENDING_ORDER_KEY); sessionStorage.removeItem(RETURN_FROM_KEY) }
    if (id && from !== undefined) {
      const f = safeFrom(from)
      if (f) sessionStorage.setItem(RETURN_FROM_KEY, f)
      else sessionStorage.removeItem(RETURN_FROM_KEY)
    }
  } catch { /* trình duyệt chặn storage — bỏ qua, vẫn theo dõi được qua mã đơn trên URL quay về */ }
}


// ── Đơn chưa trả: tiếp tục / huỷ / tạo lại ───────────────────────────

export type OrderAction = 'resume' | 'cancel' | 'renew'

/** "Tạo lại" chỉ đề nghị cho đơn gần đây — đơn cũ hơn khách tự chọn gói mới. */
export const RENEW_WINDOW_MS = 24 * 60 * 60 * 1000

const isEvoPayProvider = (p: string) => p === 'evopay_card' || p === 'evopay_bank' || p === 'evopay_paypal' || p === 'evopay_sepay' || p === 'evopay_tingee'

/**
 * Khách làm được gì với một đơn (chỉ đơn EvoPay):
 *  - đang chờ + còn hạn: tiếp tục thanh toán (khi còn QR / link) hoặc huỷ;
 *  - đang chờ nhưng quá hạn, hoặc hết hạn / lỗi / đã huỷ gần đây: tạo lại (cần báo giá đã lưu).
 * `expired` = hết hạn theo đồng hồ (server chốt trạng thái chậm hơn một nhịp) — giao diện coi là "Hết hạn".
 */
export function orderActions(
  o: Pick<BillingOrder, 'provider' | 'status' | 'expiresAt' | 'orderUrl' | 'bankTransfer' | 'quote' | 'createdAt'> & Pick<Partial<BillingOrder>, 'checkoutForm'>,
  now: number,
): { expired: boolean; actions: OrderAction[] } {
  if (!isEvoPayProvider(o.provider)) return { expired: false, actions: [] }
  const expired = o.status === 'pending' && !!o.expiresAt && new Date(o.expiresAt).getTime() <= now
  const recent = now - new Date(o.createdAt).getTime() <= RENEW_WINDOW_MS
  const canRenew = !!o.quote && recent
  if (o.status === 'pending' && !expired) {
    const canResume = o.provider === 'evopay_bank' ? !!o.bankTransfer : o.provider === 'evopay_sepay' ? !!o.checkoutForm : !!o.orderUrl
    return { expired, actions: canResume ? ['resume', 'cancel'] : ['cancel'] }
  }
  if (expired || o.status === 'expired' || o.status === 'failed' || o.status === 'cancelled') {
    return { expired, actions: canRenew ? ['renew'] : [] }
  }
  return { expired, actions: [] }
}

/** Mở lại trang thanh toán của đơn đang chờ: chuyển khoản → khung QR ở /billing/result; thẻ / PayPal → trang cổng; Cổng SePay → POST form (kiểm tên miền). */
export function resumePayment(
  o: Pick<BillingOrder, 'id' | 'provider' | 'orderUrl' | 'bankTransfer'> & Pick<Partial<BillingOrder>, 'checkoutForm'>,
  loc: Pick<Location, 'assign'> = window.location,
  doc?: FormDoc,
): void {
  if (o.checkoutForm) {
    writePendingOrder(o.id)
    return submitCheckoutForm(o.checkoutForm, doc)
  }
  if (o.bankTransfer) {
    writePendingOrder(o.id)
    return loc.assign(resultPath(readReturnFrom(), { payment: 'bank', order: o.id }))
  }
  const ok = !!o.orderUrl && (o.provider === 'evopay_paypal' ? isPayPalCheckoutUrl(o.orderUrl)
    : o.provider === 'evopay_tingee' ? isTingeeCheckoutUrl(o.orderUrl)
    : isTwoC2PCheckoutUrl(o.orderUrl))
  if (!ok) throw new Error(i18n.t('billing:errors.invalidCheckoutUrl'))
  writePendingOrder(o.id)
  loc.assign(o.orderUrl!)
}

/** Đơn hết hạn gần đây còn được nhắc "Tạo mã mới" ở thanh toàn cục (lâu hơn thì thôi, tránh nhắc mãi). */
export const LAPSED_NOTICE_MS = 30 * 60 * 1000
/** Còn dưới mức này thì thanh nhắc hiện lại dù khách đã bấm × (đơn sắp hết hạn). */
export const URGENT_LEFT_MS = 5 * 60 * 1000

/**
 * Đơn cần nhắc ở thanh toàn cục:
 *  - `pending`: đơn đang chờ, còn hạn, tiếp tục thanh toán được (đơn mới nhất);
 *  - `lapsed`: không có đơn đang chờ nhưng có đơn vừa hết hạn trong 30 phút (còn tạo lại được).
 */
export function pickPendingOrders<T extends Pick<BillingOrder, 'id' | 'provider' | 'status' | 'expiresAt' | 'orderUrl' | 'bankTransfer' | 'quote' | 'createdAt'>>(
  orders: T[],
  now: number,
): { pending?: T; lapsed?: T } {
  const byNewest = [...orders].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  const pending = byNewest.find((o) => orderActions(o, now).actions.includes('resume'))
  if (pending) return { pending }
  const lapsed = byNewest.find((o) => {
    if (o.status !== 'pending' && o.status !== 'expired') return false
    if (!o.expiresAt || !orderActions(o, now).actions.includes('renew')) return false
    const ms = now - new Date(o.expiresAt).getTime()
    return ms >= 0 && ms <= LAPSED_NOTICE_MS
  })
  return { lapsed }
}
